/**
 * Allocation Tracking API Service
 * Based on docs/spec/allocation/07-integration.md Section 8.1
 *
 * Allocation tracking is an MVP+1 feature available on Pro/Family plans.
 * Feature entitlement: feature:allocation_tracking
 */

import { api } from './api';
import type {
  PortfolioAllocationResponse,
  BucketAllocationResponse,
  AccountTargetsResponse,
  AccountTargetResponse,
  HoldingsListResponse,
  WarningsResponse,
  TemplatesResponse,
  BulkClassifyResponse,
  SetPortfolioTargetsRequest,
  SetBucketTargetsRequest,
  AssignAccountToBucketRequest,
  CreateAccountTargetRequest,
  UpdateAccountTargetRequest,
  ClassifyHoldingRequest,
  BulkClassifyHoldingsRequest,
  DismissWarningRequest,
  ApplyTemplateRequest,
  HoldingsListParams,
  WarningsListParams,
  AllocationSankeyData,
  // Historical allocation types
  HistoricalAllocation,
  PortfolioValueSeries,
  PerformanceResult,
  DriftHistory,
  HoldingHistory,
  AllocationComparison,
  HistoricalAllocationParams,
  PortfolioValueSeriesParams,
  PerformanceParams,
  DriftHistoryParams,
  AllocationComparisonParams,
  HoldingHistoryParams,
  // Manual holding CRUD types
  CreateHoldingRequest,
  UpdateHoldingRequest,
  HoldingDetail,
  // Tenant profile types (WP-ALLOC-CONFIG-1)
  TenantProfileResponse,
  TenantProfileCreate,
  TenantProfileUpdate,
} from '../types/allocation';

const BASE_PATH = '/allocation';

/**
 * Allocation API service
 *
 * All endpoints require Pro/Family subscription.
 * 402 responses indicate insufficient entitlement.
 */
export const allocationApi = {
  // ============================================================================
  // Portfolio Allocation
  // ============================================================================

  /**
   * Get current portfolio allocation by asset class
   */
  getPortfolioAllocation: () =>
    api.get<PortfolioAllocationResponse>(`${BASE_PATH}/portfolio`),

  /**
   * Set portfolio target allocations
   * @param data - Target percentages by asset class (must sum to 100)
   */
  setPortfolioTargets: (data: SetPortfolioTargetsRequest) =>
    api.put<PortfolioAllocationResponse>(`${BASE_PATH}/portfolio/targets`, data),

  // ============================================================================
  // Bucket Allocation
  // ============================================================================

  /**
   * Get current bucket allocation by strategic role
   */
  getBucketAllocation: () =>
    api.get<BucketAllocationResponse>(`${BASE_PATH}/buckets`),

  /**
   * Set bucket target allocations
   * @param data - Target percentages by strategic role (must sum to 100)
   */
  setBucketTargets: (data: SetBucketTargetsRequest) =>
    api.put<BucketAllocationResponse>(`${BASE_PATH}/buckets/targets`, data),

  /**
   * Assign an account to a strategic bucket
   * @param data - Account ID and bucket assignments
   * @note Backend only supports one role per account. If multiple roles provided,
   *       only the first role (highest percentage) is used.
   */
  assignAccountToBucket: (data: AssignAccountToBucketRequest) => {
    // Backend expects single assignment format: { account_id, strategic_role }
    // Frontend sends: { accountId, assignments: { role: pct, ... } }
    // Take the first (or highest percentage) role
    const roles = Object.entries(data.assignments);
    if (roles.length === 0) {
      return Promise.reject(new Error('No bucket assignment provided'));
    }
    // Sort by percentage descending and take the first one
    roles.sort((a, b) => b[1] - a[1]);
    const [primaryRole] = roles[0]!;

    return api.post<void>(`${BASE_PATH}/buckets/assignments`, {
      accountId: data.accountId,
      strategicRole: primaryRole,
    });
  },

  /**
   * Remove account from all bucket assignments
   * @param accountId - Account to unassign
   */
  unassignAccountFromBuckets: (accountId: string) =>
    api.delete<void>(`${BASE_PATH}/buckets/assignments/${accountId}`),

  // ============================================================================
  // Account Targets
  // ============================================================================

  /**
   * Get all account targets
   */
  getAccountTargets: () =>
    api.get<AccountTargetsResponse>(`${BASE_PATH}/targets`),

  /**
   * Get target for a specific account
   * @param accountId - Account ID
   */
  getAccountTarget: (accountId: string) =>
    api.get<AccountTargetsResponse>(`${BASE_PATH}/targets/${accountId}`),

  /**
   * Create a target for an account
   * @param data - Target configuration
   */
  createAccountTarget: (data: CreateAccountTargetRequest) =>
    api.post<AccountTargetResponse>(`${BASE_PATH}/targets`, data),

  /**
   * Update an existing account target
   * @param accountId - Account ID
   * @param data - Updated target configuration
   */
  updateAccountTarget: (accountId: string, data: UpdateAccountTargetRequest) =>
    api.put<AccountTargetResponse>(`${BASE_PATH}/targets/${accountId}`, data),

  /**
   * Delete an account target
   * @param accountId - Account ID
   */
  deleteAccountTarget: (accountId: string) =>
    api.delete<void>(`${BASE_PATH}/targets/${accountId}`),

  // ============================================================================
  // Holding Classifications
  // ============================================================================

  /**
   * Get holdings with classification info
   * @param params - Filter and pagination options
   */
  getHoldings: (params?: HoldingsListParams) =>
    api.get<HoldingsListResponse>(`${BASE_PATH}/holdings`, {
      page: params?.page,
      page_size: params?.pageSize,
      needs_review: params?.needsReview,
      asset_class: params?.assetClass,
      account_id: params?.accountId,
      include_synthetic: params?.includeSynthetic,
    }),

  /**
   * Get holdings for a specific account
   * Convenience method for account-filtered holdings
   * @param accountId - Account ID to filter by
   * @param includeSynthetic - Include synthetic holdings (default: true)
   */
  getAccountHoldings: (accountId: string, includeSynthetic = true) =>
    api.get<HoldingsListResponse>(`${BASE_PATH}/holdings`, {
      account_id: accountId,
      include_synthetic: includeSynthetic,
    }),

  /**
   * Classify a single holding
   * @param holdingId - Holding ID
   * @param data - Classification data
   */
  classifyHolding: (holdingId: string, data: ClassifyHoldingRequest) =>
    api.put<void>(`${BASE_PATH}/holdings/${holdingId}/classify`, data),

  /**
   * Bulk classify multiple holdings
   * @param data - Array of holding classifications
   */
  bulkClassifyHoldings: (data: BulkClassifyHoldingsRequest) =>
    api.post<BulkClassifyResponse>(`${BASE_PATH}/holdings/classify`, data),

  // ============================================================================
  // Warnings
  // ============================================================================

  /**
   * Get allocation warnings
   * @param params - Filter options (status, severity, type)
   */
  getWarnings: (params?: WarningsListParams) =>
    api.get<WarningsResponse>(`${BASE_PATH}/warnings`, { ...params }),

  /**
   * Dismiss a warning
   * @param warningId - Warning ID
   * @param data - Optional dismiss duration
   */
  dismissWarning: (warningId: string, data?: DismissWarningRequest) =>
    api.post<void>(`${BASE_PATH}/warnings/${warningId}/dismiss`, data),

  /**
   * Refresh warnings for current tenant (trigger recalculation)
   */
  refreshWarnings: () =>
    api.post<WarningsResponse>(`${BASE_PATH}/warnings/refresh`),

  // ============================================================================
  // Templates
  // ============================================================================

  /**
   * Get available allocation templates
   */
  getTemplates: () =>
    api.get<TemplatesResponse>(`${BASE_PATH}/templates`),

  /**
   * Apply a template to current allocation
   * @param templateId - Template ID
   * @param data - Which parts to apply (portfolio, buckets, or both)
   */
  applyTemplate: (templateId: string, data: ApplyTemplateRequest) =>
    api.post<void>(`${BASE_PATH}/templates/${templateId}/apply`, data),

  // ============================================================================
  // Visualization Data
  // ============================================================================

  /**
   * Get Sankey diagram data for allocation visualization
   */
  getSankeyData: () =>
    api.get<AllocationSankeyData>(`${BASE_PATH}/sankey`),

  // ============================================================================
  // Historical Allocation (Time-Travel Queries)
  // ============================================================================

  /**
   * Get portfolio allocation at a specific historical date
   * @param params - Target date and optional account filter
   */
  getHistoricalAllocation: (params: HistoricalAllocationParams) =>
    api.get<HistoricalAllocation>(`${BASE_PATH}/historical`, {
      target_date: params.targetDate,
      account_ids: params.accountIds,
    }),

  /**
   * Get portfolio value time series for charts
   * @param params - Date range, granularity, and optional account filter
   */
  getPortfolioValueSeries: (params: PortfolioValueSeriesParams) =>
    api.get<PortfolioValueSeries>(`${BASE_PATH}/value-series`, {
      start_date: params.startDate,
      end_date: params.endDate,
      account_ids: params.accountIds,
      granularity: params.granularity,
    }),

  /**
   * Calculate portfolio performance between two dates
   * @param params - Start date, end date, and optional account filter
   */
  getPerformance: (params: PerformanceParams) =>
    api.get<PerformanceResult>(`${BASE_PATH}/performance`, {
      start_date: params.startDate,
      end_date: params.endDate,
      account_ids: params.accountIds,
    }),

  /**
   * Get allocation drift history over time
   * @param params - Date range, granularity, and optional account filter
   */
  getDriftHistory: (params: DriftHistoryParams) =>
    api.get<DriftHistory>(`${BASE_PATH}/drift-history`, {
      start_date: params.startDate,
      end_date: params.endDate,
      account_ids: params.accountIds,
      granularity: params.granularity,
    }),

  /**
   * Compare allocation between two dates
   * @param params - Start date, end date, and optional account filter
   */
  getAllocationComparison: (params: AllocationComparisonParams) =>
    api.get<AllocationComparison>(`${BASE_PATH}/comparison`, {
      start_date: params.startDate,
      end_date: params.endDate,
      account_ids: params.accountIds,
    }),

  /**
   * Get historical snapshots for a specific holding
   * @param params - Holding ID and date range
   */
  getHoldingHistory: (params: HoldingHistoryParams) =>
    api.get<HoldingHistory>(`${BASE_PATH}/holdings/${params.holdingId}/history`, {
      start_date: params.startDate,
      end_date: params.endDate,
    }),

  // ============================================================================
  // Manual Holding CRUD (WP-MANUAL-HOLD Phase 3)
  // ============================================================================

  /**
   * Create a manual holding in a manual investment account
   * @param data - Holding data including accountId
   */
  createHolding: (data: CreateHoldingRequest) =>
    api.post<HoldingDetail>(`${BASE_PATH}/holdings`, data),

  /**
   * Get detailed holding information
   * @param holdingId - Holding ID
   */
  getHolding: (holdingId: string) =>
    api.get<HoldingDetail>(`${BASE_PATH}/holdings/${holdingId}`),

  /**
   * Update an existing manual holding
   * @param holdingId - Holding ID
   * @param data - Updated holding data
   */
  updateHolding: (holdingId: string, data: UpdateHoldingRequest) =>
    api.put<HoldingDetail>(`${BASE_PATH}/holdings/${holdingId}`, data),

  /**
   * Delete a manual holding
   * @param holdingId - Holding ID
   */
  deleteHolding: (holdingId: string) =>
    api.delete<void>(`${BASE_PATH}/holdings/${holdingId}`),

  // ============================================================================
  // Tenant Profile (WP-ALLOC-CONFIG-1)
  // ============================================================================

  /**
   * Get tenant's allocation profile
   * Returns 404 if no profile exists yet
   */
  getProfile: () =>
    api.get<TenantProfileResponse>(`${BASE_PATH}/portfolio/profile`),

  /**
   * Create tenant's allocation profile (first-time setup)
   * @param data - Initial profile configuration
   */
  createProfile: (data: TenantProfileCreate) =>
    api.post<TenantProfileResponse>(`${BASE_PATH}/portfolio/profile`, data),

  /**
   * Update tenant's allocation profile
   * @param data - Updated profile configuration
   */
  updateProfile: (data: TenantProfileUpdate) =>
    api.put<TenantProfileResponse>(`${BASE_PATH}/portfolio/profile`, data),
};

export default allocationApi;
